import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Upload, FileText, Check } from 'lucide-react';
import PartnerStatusBanner from '@/components/partner/PartnerStatusBanner';

const CATEGORIES = ['Barber', 'Nails', 'Beauty', 'Makeup', 'Massage'];
const DOC_TYPES = [
  { key: 'id', label: 'ID Document' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'right_to_work', label: 'Right to work' },
];

function ServiceForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(
    initial || { category: 'Beauty', name: '', description: '', price: 30, duration_minutes: 45, active: true },
  );
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          ...form,
          price: parseFloat(form.price),
          duration_minutes: parseInt(form.duration_minutes, 10),
        });
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={form.category} onValueChange={set('category')}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Active</Label>
          <div className="h-10 flex items-center">
            <Switch checked={!!form.active} onCheckedChange={set('active')} />
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Service name</Label>
        <Input value={form.name} onChange={set('name')} required className="h-10" data-testid="svc-name" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <Textarea value={form.description} onChange={set('description')} rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Price (£)</Label>
          <Input type="number" min="0" step="0.5" value={form.price} onChange={set('price')} required className="h-10" data-testid="svc-price" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Duration (min)</Label>
          <Input type="number" min="5" step="5" value={form.duration_minutes} onChange={set('duration_minutes')} required className="h-10" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="svc-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function ProfileTab({ profile, onChange, saving, onSave }) {
  const set = (k) => (v) => onChange((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <Card className="p-4 space-y-3 border-slate-200">
      <div className="space-y-1">
        <Label className="text-xs">Business name</Label>
        <Input value={profile.business_name || ''} onChange={set('business_name')} className="h-10" data-testid="profile-business" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Bio</Label>
        <Textarea value={profile.bio || ''} onChange={set('bio')} rows={3} placeholder="Tell customers what makes you great..." />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={profile.category || 'Beauty'} onValueChange={set('category')}>
            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Base postcode</Label>
          <Input value={profile.base_postcode || ''} onChange={set('base_postcode')} className="h-10 font-mono" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Service radius (km)</Label>
          <Input type="number" min="1" max="50" value={profile.service_radius_km || 5} onChange={set('service_radius_km')} className="h-10" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Cleanup included</Label>
          <div className="h-10 flex items-center">
            <Switch checked={!!profile.cleanup_included} onCheckedChange={set('cleanup_included')} />
          </div>
        </div>
      </div>
      <Button onClick={onSave} disabled={saving} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="profile-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save profile'}
      </Button>
    </Card>
  );
}

function ServicesTab({ services, loading, onCreate, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(null); // null = none, 'new' = creating, else service.id
  const [saving, setSaving] = useState(false);

  const submit = async (data) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await onCreate(data);
      } else {
        await onUpdate(editing, data);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };
  const initial = editing && editing !== 'new' ? services.find((s) => s.id === editing) : null;

  return (
    <Card className="p-4 border-slate-200 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Services &amp; pricing</p>
        {editing === null && (
          <Button size="sm" onClick={() => setEditing('new')} data-testid="svc-add" className="bg-rose-600 hover:bg-rose-700 text-white">
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        )}
      </div>
      {editing !== null ? (
        <ServiceForm
          initial={initial}
          saving={saving}
          onSave={submit}
          onCancel={() => setEditing(null)}
        />
      ) : loading ? (
        <p className="text-sm text-slate-500">Loading services...</p>
      ) : services.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">No services yet. Tap "Add" to create your first one.</p>
      ) : (
        <div className="space-y-2">
          {services.map((s) => (
            <div key={s.id} data-testid={`svc-row-${s.id}`} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                <p className="text-xs text-slate-500">{s.category} · £{s.price.toFixed(2)} · {s.duration_minutes} min {s.active ? '' : '· inactive'}</p>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => setEditing(s.id)} data-testid={`svc-edit-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => onDelete(s.id)} data-testid={`svc-delete-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DocumentsTab({ documents, onUpload, uploading }) {
  const docsByType = useMemo(() => {
    const map = {};
    for (const d of documents) {
      if (!map[d.document_type]) map[d.document_type] = d;
    }
    return map;
  }, [documents]);

  return (
    <Card className="p-4 border-slate-200 space-y-3">
      <p className="text-sm font-semibold text-slate-900">Documents</p>
      <p className="text-xs text-slate-500">PDF or image, max 5 MB.</p>
      <div className="space-y-2">
        {DOC_TYPES.map((d) => {
          const existing = docsByType[d.key];
          return (
            <div key={d.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
              <div className="min-w-0 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{d.label}</p>
                  {existing ? (
                    <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                      <Check className="h-3 w-3" /> {existing.original_filename}
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-400">Not uploaded</p>
                  )}
                </div>
              </div>
              <label className="shrink-0">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  data-testid={`doc-upload-${d.key}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(d.key, file);
                    e.target.value = '';
                  }}
                />
                <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer">
                  {uploading === d.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} {existing ? 'Replace' : 'Upload'}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function PartnerProfile() {
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  // Tab from URL query param
  const initialTab = typeof window !== 'undefined' && window.location.search.includes('tab=services') ? 'services' : 'profile';
  const [tab, setTab] = useState(initialTab);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, d] = await Promise.all([
        api.get('/partner/me'),
        api.get('/partner/services'),
        api.get('/partner/documents'),
      ]);
      setProfile(p.data);
      setServices(s.data);
      setDocuments(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveProfile = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        business_name: profile.business_name,
        bio: profile.bio,
        category: profile.category,
        base_postcode: profile.base_postcode,
        service_radius_km: parseFloat(profile.service_radius_km) || 5,
        cleanup_included: !!profile.cleanup_included,
      };
      const { data } = await api.put('/partner/profile', payload);
      setProfile(data);
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }, [profile]);

  const createService = useCallback(async (data) => {
    const { data: created } = await api.post('/partner/services', data);
    setServices((prev) => [created, ...prev]);
    toast.success('Service added');
  }, []);
  const updateService = useCallback(async (id, data) => {
    const { data: updated } = await api.put(`/partner/services/${id}`, data);
    setServices((prev) => prev.map((s) => (s.id === id ? updated : s)));
    toast.success('Service updated');
  }, []);
  const deleteService = useCallback(async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await api.delete(`/partner/services/${id}`);
    setServices((prev) => prev.filter((s) => s.id !== id));
    toast.success('Service deleted');
  }, []);

  const uploadDocument = useCallback(async (documentType, file) => {
    setUploading(documentType);
    try {
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('file', file);
      const { data } = await api.post('/partner/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocuments((prev) => [data, ...prev.filter((d) => d.document_type !== documentType)]);
      toast.success('Document uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  }, []);

  if (loading || !profile) {
    return <div className="text-center py-10 text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading profile...</div>;
  }

  return (
    <div className="space-y-4">
      <PartnerStatusBanner status={profile.status} businessName={profile.business_name} />

      <div className="flex gap-2 bg-white border border-slate-200 rounded-lg p-1">
        {[
          { key: 'profile', label: 'Profile' },
          { key: 'services', label: 'Services' },
          { key: 'documents', label: 'Documents' },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`profile-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 text-xs font-medium py-2 rounded-md ${tab === t.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <ProfileTab profile={profile} onChange={setProfile} saving={saving} onSave={saveProfile} />
      )}
      {tab === 'services' && (
        <ServicesTab
          services={services}
          loading={loading}
          onCreate={createService}
          onUpdate={updateService}
          onDelete={deleteService}
        />
      )}
      {tab === 'documents' && (
        <DocumentsTab documents={documents} onUpload={uploadDocument} uploading={uploading} />
      )}
    </div>
  );
}
